/**
 * analytics.ts — single tracking helper for the static-HTML pages.
 *
 * Pushes structured events onto `window.dataLayer`. GTM picks them up
 * when the container loads (see the GTM snippet in each entry HTML).
 * Until the GTM ID is configured the events just queue safely on
 * dataLayer, ready for replay once the container fires.
 *
 * The runner-game (React + Vite) has its own copy at
 * runner-game/utils/analytics.ts so the React bundle stays
 * self-contained — keep the two in sync.
 */

export type EventName =
  | "landing_view"
  | "trivia_answer"
  | "trivia_timeout"
  | "trivia_next"
  | "cta_click"
  | "game_start"
  | "game_end"
  | "endcard_cta"
  | "mute_toggle"
  | "tilt_permission";

/** Push an event onto window.dataLayer + persist to localStorage. Never throws. */
export function track(event: EventName, payload?: Record<string, unknown>): void {
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    (w.dataLayer ||= []).push({ event, ...payload });
  } catch {
    /* swallow — analytics must never break the user experience */
  }
  // Local persistence — feeds the /dashboard backup view. Capped so a
  // long-running session can't blow past localStorage's ~5MB quota.
  try {
    const KEY = "svm_events_log";
    const CAP = 5000;
    const raw = localStorage.getItem(KEY);
    const arr: unknown[] = raw ? JSON.parse(raw) : [];
    arr.push({ ts: Date.now(), event, ...payload });
    if (arr.length > CAP) arr.splice(0, arr.length - CAP);
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch {
    /* localStorage may be disabled (private mode, quota) — silent fallback */
  }
}
